import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStyles from './loginStyles';
import { Grid, TextField, Typography, Button, FormControlLabel, Checkbox, Box } from '@mui/material';
import PepsicoLogo from '../../assets/PepWise2.0/Pepwise logo_LoginPage.png';
import ErrorTriangle from '../../assets/error-triangle.svg';
import { login, getDecodedToken, landingPage } from '../../service/service';
import Loader from '../Loader/loader';
import {
	usePepwiseAuthController,
	setAuthType,
	setAuthToken,
	setAuthenticated,
	setRole,
	setCapabilities
} from 'Context/contexts/AuthContext';
import { useRedirectLogin } from 'hooks/useRedirectHooks';
import loginImageNew from '../../assets/PepWise2.0/LoginImageNew.svg'
import { useTheme } from "@mui/material/styles";

export default function LoginScreen() {
	const navigate = useNavigate();
	const theme = useTheme();
	const { redirectAfterLogin } = useRedirectLogin();
	const [controller, dispatch] = usePepwiseAuthController();
	const { isAuthenticated, accessToken, role } = controller;

	const isTokenValid = (exp?: number): boolean => {
		if (!exp) return false;
		const currentTime = Math.floor(Date.now() / 1000);
		return exp >= currentTime;
	};

	const extractCapabilities = (roles: any[] = []): string[] =>
	roles.reduce((acc: string[], role: any) => {
		(role.capabilities || []).forEach((cap: string) => {
		if (!acc.includes(cap)) acc.push(cap);
		});
		return acc;
	}, []);

	const extractRoleNames = (roles: any | any[]): string[] => {
	if (!roles) return [];
	return Array.isArray(roles) ? roles.map((r) => r.name) : [roles.name];
	};

	useEffect(() => {
	let authenticated = false;

	if (isAuthenticated && isTokenValid(accessToken?.exp)) {
		authenticated = true;
	} else {
		setAuthenticated(dispatch, false);
		setAuthToken(dispatch, null);
	}

	if (authenticated) return;

	const auth = getDecodedToken();
	if (!auth || !isTokenValid(auth.exp)) return;

	const roles = auth.role || [];
	const capabilities = extractCapabilities(roles);
	const roleNames = extractRoleNames(roles);

	setAuthType(dispatch, 'local');
	setAuthToken(dispatch, auth);
	setRole(dispatch, roleNames);
	setCapabilities(dispatch, capabilities);
	setAuthenticated(dispatch, true);

	navigate(landingPage[role] || '/');
	}, []);

	const classes = useStyles();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [emailError, setEmailError] = useState('');
	const [passwordError, setPasswordError] = useState('');
	const [pageLoad, setPageLoad] = useState(false);

	const handleAuthSuccess = (auth: any, dispatch: any) => {
	const roles = auth.role || [];

	setAuthType(dispatch, 'local');
	setAuthToken(dispatch, auth);
	setRole(dispatch, extractRoleNames(roles));
	setCapabilities(dispatch, extractCapabilities(roles));
	setAuthenticated(dispatch, true);

	redirectAfterLogin();
	};

	const handleAuthError = (res: any) => {
	const status = res?.response?.status;
	if (status !== 401 && status !== 404) return;

	const message = res.response?.data?.data?.message;
	if (!message) return;

	if (message.email) {
		setEmailError('Invalid Email');
	} else if (message.msg) {
		setPasswordError(message.msg);
	}
	};

	async function loginFunc(event: any) {
	event.preventDefault();
	setPageLoad(true);

	try {
		if (validate()) return;

		const res = await login({ email, password });

		if (!res?.success) {
		handleAuthError(res);
		return;
		}

		localStorage.setItem('sso-login', 'false');

		const auth = getDecodedToken();
		if (auth) {
		handleAuthSuccess(auth, dispatch);
		}
	} finally {
		setPageLoad(false);
	}
	}


	const validate = () => {
		let isError = false;
		const errors = {
			emailError: '',
			passwordError: ''
		};

		if (!email) {
			isError = true;
			errors.emailError = 'Email is required!';
		} else if (!/\S+@\S+\.\S+/.test(email)) {
			isError = true;
			errors.emailError = 'Email is invalid!';
		}

		if (!password) {
			isError = true;
			errors.passwordError = 'Password is required!';
		}

		setEmailError(errors.emailError);
		setPasswordError(errors.passwordError);

		return isError;
	};

	return (
		<Grid
			container
			sx={{
				height: '100%',
				position: 'fixed',
				width: '100% !important',
				maxHeight: '100%',
				fontFamily: 'Montserrat'
			}}
		>
			{/* <Grid item xs={6} className={classes.firstGrid}></Grid> */}
			<Grid
				item
				xs={6}
				sx={{
					background: `url(${loginImageNew}) no-repeat`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					height: '100vh',
					width: '100%',
					'@media (max-width: 600px)': {
						backgroundSize: 'contain',
						backgroundPosition: 'center',
					},
				}}
			></Grid>

			<Grid
				item
				xs={6}
				className={classes.loginForm}
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
					pl: "19px"
				}}
			>
				<form onSubmit={loginFunc}>
					<Box
						className={classes.center}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%'
						}}
					>
						<img id="logoGridContainer1" src={PepsicoLogo} className={classes.logo} />
						<Typography
							sx={{
								fontFamily: 'Montserrat',
								fontSize: '14px',
								textAlign: 'left',
								color: `${theme.palette.login.text.tagLine} !important`,
								fontWeight: 500,
								lineHeight: '25px',
								marginLeft: '14%',
								marginBottom: '10px'
							}}
						>
							Observability and Monitoring Framework
						</Typography>
						<Typography
							sx={{
								clear: 'both',
								display: 'block',
								textAlign: 'left',
								color: `${theme.palette.login.text.main}`,
								width: '80%',
								fontSize: '24px',
								marginTop: '40px',
								marginLeft: '14%',
								fontFamily: 'Montserrat',
								fontWeight: 400
							}}
						>
							Login to continue
						</Typography>
						<TextField
							id="searchFieldTextUser"
							variant="standard"
							autoComplete="off"
							placeholder="Email / User Id"
							className={emailError ? classes.errorText : classes.textfield}
							value={email}
							onChange={(e) => {
								setEmail(e.target.value.trim());
							}}
							inputProps={{
								style: {
									height: '35px',
									paddingRight: '20px'
								}
							}}
						/>
						<TextField
							id="searchFieldpassword"
							autoComplete="off"
							type="password"
							variant="standard"
							placeholder="Password"
							value={password}
							onChange={(e) => {
								setPassword(e.target.value.trim());
							}}
							className={passwordError ? classes.errorText : classes.textfield}
							inputProps={{
								style: {
									height: '35px',
									paddingRight: '20px'
								}
							}}
						/>
						{(emailError || passwordError) && (
							<div className={classes.pwdErr}>
								<img src={ErrorTriangle} />
								Invalid credentials. Please try again.
							</div>
						)}
						<Box
							sx={{
								marginLeft: '14% !important',
								marginTop: '15px',
								width: '85%',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center'
							}}
						>
							<FormControlLabel
								className={classes.rememberTxt}
								label="Remember me"
								control={
									<Checkbox
										sx={{
											color: `${theme.palette.login.background?.button}`,
											'&.Mui-checked': {
												color: `${theme.palette.login.background?.button}`
											}
										}}
									/>
								}
							/>
							<a href="#" onClick={(e) => e.preventDefault()} className={classes.forgotTxt}>
								Forgot password?
							</a>
						</Box>
						<Button
							id="#loginbutton"
							variant="contained"
							className={classes.loginBtn}
							type="submit"
							disabled={pageLoad}
						>
							CONTINUE
						</Button>
					</Box>
				</form>
			</Grid>

			{pageLoad && <Loader />}
		</Grid>
	);
}
